import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import GlassCard from "../../../components/common/GlassCard";

function getStatusColor(status) {
  switch (status) {
    case "active":
      return "success";
    case "enrolled":
      return "info";
    case "completed":
      return "default";
    case "withdrawn":
      return "error";
    default:
      return "default";
  }
}

function ProgramEnrollmentCard({ enrollments, onAddEnrollment, onDeleteEnrollment }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeEnrollmentId, setActiveEnrollmentId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setMenuAnchor(event.currentTarget);
    setActiveEnrollmentId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveEnrollmentId(null);
  };

  return (
    <GlassCard
      title="Program Enrollment"
      icon={<SchoolIcon color="info" />}
      onAdd={onAddEnrollment}
      addLabel="Add Enrollment"
    >
      {enrollments?.length ? (
        enrollments.map((enrollment) => (
          <Box
            key={enrollment.id}
            mb={1}
            sx={{
              p: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
            data-testid={`enrollment-item-${enrollment.id}`}
            data-enrollment-status={enrollment.status}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500}>
                  {enrollment.program_name || `Program ${enrollment.program_id}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Enrolled: {enrollment.enrollment_date ? new Date(enrollment.enrollment_date).toLocaleDateString() : "N/A"}
                </Typography>
                {enrollment.completion_date && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Completed: {new Date(enrollment.completion_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
              <Box display="flex" gap={0.5} alignItems="center">
                <Chip 
                  label={enrollment.status} 
                  size="small" 
                  color={getStatusColor(enrollment.status)} 
                  variant="outlined"
                  data-testid={`enrollment-status-chip-${enrollment.id}`}
                  aria-label={`Enrollment status: ${enrollment.status}`}
                />
                {onDeleteEnrollment && (
                  <Tooltip title="Options">
                    <IconButton 
                      size="small" 
                      onClick={(e) => handleMenuOpen(e, enrollment.id)}
                      data-testid={`enrollment-menu-button-${enrollment.id}`}
                      aria-label="Open enrollment options"
                      data-action="enrollment-menu"
                      data-enrollment-id={enrollment.id}
                    >
                      <MoreVertIcon 
                        aria-label="Open enrollment options" 
                        fontSize="small"
                        data-testid={`enrollment-options-icon-${enrollment.id}`}
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No program enrollments
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (activeEnrollmentId && onDeleteEnrollment) {
              onDeleteEnrollment(activeEnrollmentId);
            }
          }}
          data-testid={`enrollment-delete-menu-item-${activeEnrollmentId}`}
          aria-label="Delete enrollment"
          data-action="delete-enrollment"
          data-enrollment-id={activeEnrollmentId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

ProgramEnrollmentCard.propTypes = {
  enrollments: PropTypes.array,
  onAddEnrollment: PropTypes.func.isRequired,
  onDeleteEnrollment: PropTypes.func,
};

ProgramEnrollmentCard.defaultProps = {
  enrollments: [],
};

export default ProgramEnrollmentCard;
